import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Save } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ProgressBar from "@/components/apply/ProgressBar";
import Step1 from "@/components/apply/Step1";
import Step2 from "@/components/apply/Step2";
import Step3 from "@/components/apply/Step3";
import Step4 from "@/components/apply/Step4";
import Review from "@/components/apply/Review";
import ConsentMicrocopy from "@/components/ConsentMicrocopy";
import { apiClient } from "@/lib/api";
import { buildInitialForm, paramsToObject } from "@/lib/applyForm";

const STEP_LABELS = ["Life Stage", "Financial", "Interests", "Contact", "Review"];

const numOr = (v, fallback = 0) => (v === "" || v === null ? fallback : Number(v));
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export default function Apply() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialForm = useMemo(() => buildInitialForm(params), [params]);
  const sourceContext = useMemo(() => paramsToObject(params), [params]);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [saveResumeState, setSaveResumeState] = useState("idle"); // idle|loading|done

  const validateStep = (s) => {
    const e = {};
    if (s === 1) {
      if (form.life_stage_data.age === "" || form.life_stage_data.age < 0 || form.life_stage_data.age > 110)
        e.age = "Age must be 0–110.";
      if (form.life_stage_data.dependents < 0 || form.life_stage_data.dependents > 10)
        e.dependents = "Use 0–10.";
      if (!form.life_stage_data.goals.length)
        e.goals = "Pick at least one goal.";
    } else if (s === 2) {
      if (form.financial_data.income === "" || form.financial_data.income < 0)
        e.income = "Income is required.";
    } else if (s === 3) {
      if (!form.product_interests.length)
        e.product_interests = "Pick at least one — or 'I'm not sure'.";
    } else if (s === 4) {
      const c = form.contact;
      if (!c.full_name || c.full_name.trim().length < 2) e.full_name = "Required.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)) e.email = "Enter a valid email.";
      const digits = (c.phone || "").replace(/\D/g, "");
      if (digits.length < 10) e.phone = "Enter a 10-digit US phone.";
      if (!c.state) e.state = "Select your state.";
      if (!c.consent) e.consent = "Consent is required to proceed.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (validateStep(step)) setStep((s) => Math.min(s + 1, 5));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    if (!validateStep(4)) {
      setStep(4);
      return;
    }
    setSubmitting(true);
    setServerError("");
    try {
      const payload = {
        source: sourceContext.source || "direct",
        source_context: sourceContext,
        life_stage_data: {
          age: numOr(form.life_stage_data.age),
          marital_status: form.life_stage_data.marital_status,
          dependents: numOr(form.life_stage_data.dependents),
          goals: form.life_stage_data.goals,
          time_horizon: form.life_stage_data.time_horizon,
        },
        financial_data: {
          income: numOr(form.financial_data.income),
          retirement_savings: numOrNull(form.financial_data.retirement_savings),
          existing_life_coverage: numOrNull(form.financial_data.existing_life_coverage),
          debt: numOrNull(form.financial_data.debt),
          monthly_savings: numOrNull(form.financial_data.monthly_savings),
          risk_comfort: form.financial_data.risk_comfort,
        },
        product_interests: form.product_interests,
        contact: form.contact,
      };
      const { data } = await apiClient.post("/leads", payload);
      navigate(`/apply/success?ref=${encodeURIComponent(data.lead_id)}`);
    } catch (e) {
      setServerError(
        e.response?.data?.detail
          ? "Some fields are invalid — please review and try again."
          : "We couldn't submit right now. Please try again in a moment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const saveAndResume = async () => {
    const email = form.contact.email;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors({ ...errors, email: "Enter an email so we can save your progress." });
      setStep(4);
      return;
    }
    setSaveResumeState("loading");
    try {
      await apiClient.post("/leads/partial", {
        email,
        source: "save_and_resume",
        source_context: { ...sourceContext, step },
      });
      setSaveResumeState("done");
    } catch {
      setSaveResumeState("idle");
    }
  };

  return (
    <div data-testid="apply-page" className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12">
          <span className="lw-eyebrow">Application</span>
          <h1
            data-testid="apply-headline"
            className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.1]"
          >
            A few questions before your{" "}
            <span className="italic">advisor reaches out</span>.
          </h1>
          <p className="mt-3 text-base text-lw-muted leading-relaxed max-w-2xl">
            Takes about 3 minutes. Nothing is shared, sold, or used outside of
            preparing for your consultation.
          </p>

          <div className="gold-hairline opacity-60 my-8" />
          <ProgressBar step={step} total={5} labels={STEP_LABELS} />

          <div className="bg-white rounded-2xl border border-lw-navy/10 p-6 sm:p-8">
            {step === 1 && (
              <Step1
                data={form.life_stage_data}
                onChange={(d) => setForm({ ...form, life_stage_data: d })}
                errors={errors}
              />
            )}
            {step === 2 && (
              <Step2
                data={form.financial_data}
                onChange={(d) => setForm({ ...form, financial_data: d })}
                errors={errors}
              />
            )}
            {step === 3 && (
              <Step3
                data={form.product_interests}
                onChange={(pi) => setForm({ ...form, product_interests: pi })}
                errors={errors}
              />
            )}
            {step === 4 && (
              <Step4
                data={form.contact}
                onChange={(d) => setForm({ ...form, contact: d })}
                errors={errors}
              />
            )}
            {step === 5 && <Review form={form} onEditStep={setStep} />}

            {serverError && (
              <p
                data-testid="apply-server-error"
                className="mt-5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
              >
                {serverError}
              </p>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              {step > 1 && (
                <button
                  type="button"
                  onClick={goBack}
                  data-testid="apply-back"
                  className="btn-ghost-navy"
                >
                  <ArrowLeft size={18} /> Back
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {step < 5 ? (
                <button
                  type="button"
                  onClick={goNext}
                  data-testid="apply-continue"
                  className="btn-gold"
                >
                  Continue <ArrowRight size={18} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="apply-submit"
                  className="btn-gold disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="animate-spin" size={18} /> Submitting…
                    </>
                  ) : (
                    <>Submit Application <ArrowRight size={18} /></>
                  )}
                </button>
              )}
            </div>
          </div>

          {step === 3 && (
            <div className="mt-6" data-testid="apply-consent">
              <ConsentMicrocopy withPhone />
            </div>
          )}

          <div className="mt-6 flex items-center gap-3">
            <button
              type="button"
              onClick={saveAndResume}
              disabled={saveResumeState === "loading"}
              data-testid="apply-save-resume"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-lw-navy/70 hover:text-lw-navy transition-colors"
            >
              <Save size={14} />
              {saveResumeState === "loading"
                ? "Saving…"
                : saveResumeState === "done"
                  ? "Saved — check your inbox."
                  : "Save & resume later"}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
